import { Info } from 'lucide-react';
import { PreflightCopy } from '@signalvote/shared';
import { useFilingSendStatus } from '@/hooks/queries/useFilingSend';
import { formatDate } from '../utils';
import { withReceivingOffice } from './receivingOffice';
import { getDateStringInTimezone } from '@/lib/calendarUtils';
import { campaignTimeZoneId } from '@/lib/campaignDateTimeInput';
import { answered } from '@/lib/reportsRead';

interface SentReportEditWarningProps {
  reportId: string;
  /** The served receiving-office name (`profile.portalName`). */
  officeName: string;
}

/**
 * Edit-after-send warning. Sending never changes the report's status (it stays Ready until the
 * treasurer confirms it filed), so nothing stops a treasurer editing a report they already emailed —
 * and the office holds the version they sent. There is no partial amendment: a change means re-sending
 * the whole corrected report (the self-contained-upload amendment model; a changed file mints a new
 * hash, which the double-send guard accepts). Self-hides until something has been sent.
 */
export function SentReportEditWarning({ reportId, officeName }: SentReportEditWarningProps) {
  const sendStatus = answered(useFilingSendStatus(reportId));
  if (!sendStatus?.hasBeenSent) return null;

  // The send date in the CAMPAIGN timezone — an evening send must not read as the next (UTC) day.
  const sentOn = sendStatus.sentAt
    ? formatDate(getDateStringInTimezone(sendStatus.sentAt, campaignTimeZoneId()))
    : 'an earlier date';
  const copy = withReceivingOffice(PreflightCopy.SentReportEditWarning, officeName).replace('{date}', sentOn);

  return (
    <div
      className="flex items-start gap-2 rounded-lg border border-blue-200 bg-blue-50 p-3"
      data-testid="sent-report-edit-warning"
    >
      <Info className="mt-0.5 h-4 w-4 shrink-0 text-blue-700" aria-hidden="true" />
      <p className="text-sm text-blue-900">{copy}</p>
    </div>
  );
}
